import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { CurrencyPipe } from "@angular/common";
import { ToastrService } from "ngx-toastr";
import { InventoryItem, InventoryService } from "../../../inventory";
import { Account, AccountService } from "../../../../account/account";

@Component({
  selector: "common-machine-create-wizard-pricing",
  templateUrl: "./machine-create-wizard-pricing.component.html",
  styleUrls: ["./machine-create-wizard-pricing.component.scss"],
  providers: [CurrencyPipe],
})
export class MachineCreateWizardPricingComponent implements OnInit {
  @Input() machineId: string;
  @Output() pricingSaved = new EventEmitter<any>();

  // Machine data
  entity: InventoryItem | null = null;
  machineLoading: boolean = false;
  actualMachineId: string = "";
  partitionKey: string = "";
  account: Account | null = null;

  // Wizard flow state
  currentStep: "rentalRates" | "rentToOwn" | "sellItNow" = "rentalRates";
  showDropdown: boolean = false;

  // Rental rates data (from details component)
  confirmedData: any = {
    rentalRates: {
      monthly: null,
      weekly: null,
      daily: null,
    },
  };
  analysisResult: any = null; // Will be loaded from machine data

  // Form data
  loading: boolean = false;
  validationErrors: string[] = [];

  // Sell it now options
  sellReason: string = "";

  // RPO options
  monthlyDuration: number = 1; // Default RPO duration
  totalRentApplied: number = 0;
  renterPayoffPrice: number = 0;
  rubblConversionFee: number = 0;
  ownerTotalProceeds: number = 0;

  comparisonTable = {
    headers: [],
    rows: [],
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private currencyPipe: CurrencyPipe,
    public inventoryService: InventoryService,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.loadMachineFromParams();
  }

  private loadMachineFromParams(): void {
    // Get machine ID from route params and partitionKey from query params
    const machineId = this.route.snapshot.params["id"];

    this.route.queryParams.subscribe(params => {
      const partitionKey = params["partitionKey"];

      if (machineId && partitionKey) {
        this.loadMachine(machineId, partitionKey);
      } else {
        this.toastrService.error("Machine ID or partition key not found. Please try creating the machine again.");
        this.router.navigate(["/owner/fleet"]);
      }
    });
  }

  private loadMachine(machineId: string, partitionKey: string): void {
    this.machineLoading = true;
    this.actualMachineId = machineId;
    this.partitionKey = partitionKey;

    this.inventoryService.getWithPartitionKey(machineId, partitionKey).subscribe({
      next: (machine: InventoryItem) => {
        this.entity = machine;
        this.machineLoading = false;

        // Debug: Log the loaded machine flags
        console.log("Loaded machine in pricing component:", {
          rpoEnabled: this.entity.rpoEnabled,
          buyItNowEnabled: this.entity.buyItNowEnabled,
          rentalRate: this.entity.rentalRate,
        });

        // Load account information
        if (this.entity.accountId) {
          this.accountService.get(this.entity.accountId).subscribe(account => {
            this.account = account;
          });
        }

        // Load rental rates from machine data if available
        if (this.entity && (this.entity.rentalRate != null || this.entity.rentalRateWeekly != null)) {
          this.confirmedData.rentalRates.monthly = this.entity.rentalRate != null ? this.entity.rentalRate.toString() : "";
          this.confirmedData.rentalRates.weekly = this.entity.rentalRateWeekly != null ? this.entity.rentalRateWeekly.toString() : "";
          if (this.confirmedData.rentalRates.monthly && !this.confirmedData.rentalRates.weekly) {
            this.applyWeeklyFromMonthlyForWizard();
          }
        }

        // Initialize RTO calculations if RTO is enabled (like original component)
        if (this.entity && this.entity.rpoEnabled) {
          this.calculateRpoMetrics();
        }
      },
      error: error => {
        console.error("Error loading machine:", error);
        this.toastrService.error("Failed to load machine details. Please try again.");
        this.machineLoading = false;
        this.router.navigate(["/owner/fleet"]);
      },
    });
  }

  // Validation methods
  get isRentSelected(): boolean {
    return this.entity && this.entity.rentalRate !== -1;
  }

  canProceedFromRentalRates(): boolean {
    // For rental rates step, check if weekly or monthly rate is provided
    return !!this.confirmedData.rentalRates.weekly || !!this.confirmedData.rentalRates.monthly;
  }

  canProceedFromSellItNow(): boolean {
    // For sell it now step, validate selling price
    return !!(this.entity && this.entity.buyItNowPrice && this.entity.buyItNowPrice > 0);
  }

  // Navigation methods
  onBack(): void {
    // Navigate back to details step
    this.router.navigate(["/owner/fleet/wizard", "details"]);
  }

  // This method is no longer used - navigation is handled by specific step methods

  // Methods moved from details component for handling rental rates
  hasSuggestedRates(): boolean {
    return this.analysisResult && this.analysisResult.monthlyRate && this.analysisResult.monthlyRate > 0;
  }

  get suggestedWeeklyRateFromMonthly(): number {
    if (!this.analysisResult?.monthlyRate || this.analysisResult.monthlyRate <= 0) {
      return 0;
    }
    return Math.round(Number(this.analysisResult.monthlyRate) / 3);
  }

  acceptSuggestedRates(): void {
    if (this.analysisResult) {
      this.confirmedData.rentalRates.monthly = this.analysisResult.monthlyRate.toString();
      this.applyWeeklyFromMonthlyForWizard();

      this.entity.rentalRate = this.analysisResult.monthlyRate;
      const w = this.parseRateToInt(this.confirmedData.rentalRates.weekly);
      this.entity.rentalRateWeekly = w !== undefined ? w : null;
    }
    this.persistRentalRatesAndProceed();
  }

  rejectSuggestedRates(): void {
    this.showDropdown = true;
    if (this.hasSuggestedRates()) {
      this.confirmedData.rentalRates.monthly = this.analysisResult.monthlyRate.toString();
      this.applyWeeklyFromMonthlyForWizard();
    }
  }

  saveRentalRates(): void {
    // Apply rental rates to entity
    if (this.confirmedData.rentalRates.monthly) {
      this.entity.rentalRate = parseInt(this.confirmedData.rentalRates.monthly.replace(/,/g, ""), 10);
    }
    if (this.confirmedData.rentalRates.weekly) {
      this.entity.rentalRateWeekly = parseInt(this.confirmedData.rentalRates.weekly.replace(/,/g, ""), 10);
    }

    this.persistRentalRatesAndProceed();
  }

  onMonthlyRentalRateChange(): void {
    this.applyWeeklyFromMonthlyForWizard();
  }

  onCustomRentalRateChange(): void {
    if (this.confirmedData.rentalRates.weekly) {
      const weeklyValue = parseFloat(this.confirmedData.rentalRates.weekly.replace(/,/g, "")) || 0;
      if (!this.confirmedData.rentalRates.monthly && weeklyValue > 0) {
        this.confirmedData.rentalRates.monthly = Math.round(weeklyValue * 3).toString();
      }
    }
  }

  private applyWeeklyFromMonthlyForWizard(): void {
    const m = this.confirmedData.rentalRates.monthly;
    if (m === null || m === undefined || String(m).trim() === "") {
      this.confirmedData.rentalRates.weekly = "";
      return;
    }
    const monthly = parseFloat(String(m).replace(/,/g, ""));
    if (!Number.isFinite(monthly) || monthly <= 0) {
      return;
    }
    this.confirmedData.rentalRates.weekly = Math.round(monthly / 3).toString();
  }

  private parseRateToInt(value: string | null | undefined): number | undefined {
    if (value === null || value === undefined || String(value).trim() === "") {
      return undefined;
    }
    const n = parseInt(String(value).replace(/,/g, ""), 10);
    return Number.isFinite(n) ? n : undefined;
  }

  calculateDailyEarnings(dailyRate: number): number {
    if (!dailyRate) return 0;
    return dailyRate * 0.85; // Assuming 15% commission
  }

  calculateWeeklyEarnings(weeklyRate: number): number {
    if (!weeklyRate) return 0;
    return weeklyRate * 0.85; // Assuming 15% commission
  }

  calculateMonthlyEarnings(monthlyRate: number): number {
    if (!monthlyRate) return 0;
    return monthlyRate * 0.85; // Assuming 15% commission
  }

  getCustomDailyEarnings(): number {
    if (!this.confirmedData.rentalRates.daily) return 0;
    const dailyRate = parseFloat(this.confirmedData.rentalRates.daily.replace(/,/g, "")) || 0;
    return dailyRate * 0.85;
  }

  getCustomWeeklyEarnings(): number {
    if (!this.confirmedData.rentalRates.weekly) return 0;
    const weeklyRate = parseFloat(this.confirmedData.rentalRates.weekly.replace(/,/g, "")) || 0;
    return weeklyRate * 0.85;
  }

  getCustomMonthlyEarnings(): number {
    if (!this.confirmedData.rentalRates.monthly) return 0;
    const monthlyRate = parseFloat(this.confirmedData.rentalRates.monthly.replace(/,/g, "")) || 0;
    return monthlyRate * 0.85;
  }

  private persistRentalRatesAndProceed(): void {
    const monthly = this.confirmedData.rentalRates.monthly
      ? parseInt(this.confirmedData.rentalRates.monthly.replace(/,/g, ""), 10)
      : null;
    const weekly = this.confirmedData.rentalRates.weekly
      ? parseInt(this.confirmedData.rentalRates.weekly.replace(/,/g, ""), 10)
      : null;

    const patchOperations: { op: string; path: string; value: any }[] = [
      { op: "replace", path: "/rentalRate", value: monthly },
      { op: "replace", path: "/rentalRateWeekly", value: weekly },
    ];

    this.loading = true;
    this.inventoryService.jsonPatchWithPartitionKey(this.actualMachineId, patchOperations, this.partitionKey).subscribe({
      next: () => {
        this.loading = false;
        this.proceedToNextStep();
      },
      error: err => {
        this.loading = false;
        console.error("Failed to save rental rates:", err);
        this.toastrService.error("Failed to save rental rates. Please try again.");
      },
    });
  }

  private proceedToNextStep(): void {
    // Check if rent-to-own is enabled, proceed there first
    if (this.entity && this.entity.rpoEnabled) {
      this.currentStep = "rentToOwn";
      this.calculateRpoMetrics();
      window.scrollTo(0, 0);
    } else if (this.entity && this.entity.buyItNowEnabled) {
      this.currentStep = "sellItNow";
      window.scrollTo(0, 0);
    } else {
      // Skip to options if no rent-to-own or sell it now (options precedes attachments in wizard)
      this.proceedToOptions();
    }
  }

  proceedToOptions(): void {
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.split("/").slice(0, -1).join("/");
    this.router.navigate([baseUrl, "options"], {
      queryParams: { partitionKey: this.partitionKey },
    });
  }

  goBackToRentalRates(): void {
    this.currentStep = "rentalRates";
    window.scrollTo(0, 0);
  }

  proceedToSellItNow(): void {
    if (this.entity && this.entity.buyItNowEnabled) {
      this.currentStep = "sellItNow";
      window.scrollTo(0, 0);
    } else {
      this.proceedToOptions();
    }
  }

  goBackToRentToOwn(): void {
    this.currentStep = "rentToOwn";
    window.scrollTo(0, 0);
  }

  onRpoPriceChange(purchasePrice: any): void {
    if (typeof purchasePrice === "string") {
      this.entity.purchasePrice = parseFloat(purchasePrice.replace(/,/g, "")) || 0;
    } else {
      this.entity.purchasePrice = purchasePrice;
    }
    this.calculateRpoMetrics();
  }

  // Update RPO calculations when the rental duration changes
  onRpoDurationChange(duration: string): void {
    this.monthlyDuration = parseInt(duration, 10) || 1;
    this.calculateRpoMetrics();
  }

  // Calculate RPO-related metrics
  calculateRpoMetrics(): void {
    const rentalDeductionRateConst = 0.8;
    const conversionFeeConst = 0.075;

    const rentDeduction = (this.entity.rentalRate || 0) * rentalDeductionRateConst * this.monthlyDuration;
    const adjustedPrice = (this.entity.purchasePrice || 0) - rentDeduction;
    const conversionFee = adjustedPrice * conversionFeeConst;
    const ownerTotalProceeds = adjustedPrice - conversionFee;

    this.comparisonTable.rows = [
      {
        columns: [
          {
            label: "Monthly Rental Rate<div class='fs-14 font-weight-light'>This is the rate used to calculate the monthly rental deduction.</div>",
          },
          {
            label: this.entity.rentalRate
              ? `<b class='mt-3 mt-lg-0'>${this.currencyPipe.transform(this.entity.rentalRate, "USD", "symbol", "1.0-0")}</b>`
              : `<b class='mt-3 mt-lg-0'>-</b>`,
          },
        ],
      },
      {
        columns: [
          {
            label: `Total Rent Applied<br/><div class='fs-14 font-weight-light'>80% of paid rent will be applied payoff price. This amount is ${this.currencyPipe.transform(
              this.entity.rentalRate * rentalDeductionRateConst,
              "USD",
              "symbol",
              "1.0-0",
            )} per month.</div>`,
          },
          {
            label: `<span class='text-primary mt-3 mt-lg-0'>(${this.currencyPipe.transform(rentDeduction, "USD", "symbol", "1.0-0")})</span>`,
          },
        ],
      },
      {
        columns: [
          { label: "Renter Payoff Price<div class='fs-14 font-weight-light'>This is the renter's final purchase price.</div>" },
          { label: adjustedPrice > 0 ? `<b class='mt-3 mt-lg-0'>${this.currencyPipe.transform(adjustedPrice, "USD", "symbol", "1.0-0")}</b>` : "-" },
        ],
      },
      {
        columns: [
          { label: "<b>Rubbl Conversion Fee</b><br/><div class='fs-14 font-weight-light'>Commission is 7.5% of the renter payoff price price.<div>" },
          {
            label:
              conversionFee > 0
                ? `<b><span class='text-primary mt-3 mt-lg-0'>(${this.currencyPipe.transform(conversionFee, "USD", "symbol", "1.0-0")})</span></b>`
                : "-",
          },
        ],
      },
      {
        columns: [
          { label: "<b>Owner Total Proceeds</b><div class='fs-14 font-weight-light'>This is the final net sale proceeds you will receive.</div>" },
          {
            label:
              ownerTotalProceeds > 0 ? `<b class="fs-18 text-success">${this.currencyPipe.transform(ownerTotalProceeds, "USD", "symbol", "1.0-0")}</b>` : "-",
          },
        ],
      },
    ];
  }
}
